import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeSlash, Lightning } from "@phosphor-icons/react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      if (err.code === "ECONNABORTED" || String(err.message || "").toLowerCase().includes("timeout")) {
        toast.error("Server is taking too long. Check that the backend is running.");
      } else if (!err.response) {
        toast.error("Cannot reach server. Check your connection and backend URL.");
      } else {
        toast.error(err.response?.data?.detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#111827] text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="font-heading font-black text-3xl tracking-tighter">
          LEAD<span className="text-[#FF2A00]">/</span>PILOT
        </div>
        <div className="relative z-10">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF2A00] mb-4">
            01 — Capture · 02 — Engage · 03 — Convert
          </div>
          <h1 className="font-heading font-black text-5xl xl:text-6xl leading-[0.95] tracking-tighter">
            Every lead.<br />
            Every channel.<br />
            <span className="text-[#FF2A00]">Zero leaks.</span>
          </h1>
          <p className="mt-6 text-[#9CA3AF] max-w-md leading-relaxed">
            Unified lead pipeline from IndiaMART, Meta Ads & Google Drive — auto-routed to your salespeople with 5-stage WhatsApp & Email follow-ups. Managed by you.
          </p>
        </div>
        <div className="text-xs text-[#6B7280] font-mono">
          v1.0 / leadpilot.os
        </div>
        {/* grid texture */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center bg-white p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden font-heading font-black text-3xl tracking-tighter text-[#111827] mb-8">
            LEAD<span className="text-[#002FA7]">/</span>PILOT
          </div>

          <div className="inline-flex items-center gap-2 bg-blue-50 text-[#002FA7] text-xs font-bold px-2.5 py-1 uppercase tracking-widest border border-blue-200 mb-6">
            <Lightning size={12} weight="fill" /> Sign in
          </div>

          <h2 className="font-heading font-black text-4xl tracking-tighter text-[#111827] leading-none mb-2">
            Welcome back.
          </h2>
          <p className="text-[#6B7280] mb-8">Sign in to manage your lead pipeline.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B7280]">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1 w-full bg-white border border-[#D1D5DB] rounded-none px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] focus:outline-none text-[#111827]"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B7280]">Password</label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="w-full bg-white border border-[#D1D5DB] rounded-none px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] focus:outline-none text-[#111827]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#111827] transition-colors"
                >
                  {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#002FA7] text-white font-bold px-5 py-3 rounded-none hover:bg-[#002280] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
